import React, { useState } from "react";
import { Alert, Bank, Button, Card, Flex, Modal, Radio, Tag, Tooltip, Typography } from "@payping/web-components";
import { CloseCircleOutlined, FiTrash, InfoCircleFilled } from "@payping/icons";
import { useBankName } from "@payping/web-components/components/Bank/desktop";
import { formatIBAN } from "@/utils";
import { useAppTranslation } from "@/utils/useAppTranslation";

interface ShebaCardProps {
  sheba: string;
  status?: "active" | "pending" | "rejected";
  statusText?: string;
  errorMessage?: string;
  onDelete?: () => void;
  showBankPrefix?: boolean;
  onClickRadio?: () => void;
  checked?: boolean;
  disabled?: boolean;
  title?: string;
  showSettlementWarning?: boolean;
  disableDelete?: boolean;
  hideDeleteIcon?: boolean;
  onRetry?: () => void;
  retryLoading?: boolean;
  isActiveIBAN?: boolean;
}

const getStatusColor = (statusType: string) => {
  switch (statusType) {
    case "active":
      return "success";
    case "pending":
    case "in_progress":
      return "warning";
    case "rejected":
      return "error";
    case "requested":
      return "warning";
    case "settlement_ready":
      return "success";
    case "active_settlement":
      return "success";
    case "approved":
      return "success";
    default:
      return "default";
  }
};

interface CardHeaderProps {
  title?: string;
  onDelete?: () => void;
  hideDeleteIcon?: boolean;
  checked?: boolean;
  disableDelete?: boolean;
  isActiveIBAN?: boolean;
  onDeleteClick: (e: React.MouseEvent) => void;
}

const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  onDelete,
  hideDeleteIcon,
  checked,
  disableDelete,
  isActiveIBAN,
  onDeleteClick,
}) => {
  const { t } = useAppTranslation();

  return (
    <Flex justify="space-between" gap="16px" className="tw-w-full">
      <Flex className="tw-mt-1" align="center" gap="6px">
        {title ? (
          <Typography.Text className="tw-text-gray-900 tw-text-sm tw-font-normal tw-leading-5">{title}</Typography.Text>
        ) : (
          <div className="tw-w-10"></div>
        )}
      </Flex>

      <Flex gap="8px">
        {onDelete && !hideDeleteIcon && !checked && (
          <Tooltip title={disableDelete && isActiveIBAN ? t("profile.bank.cannotDeleteActiveIBAN") : ""}>
            <Button disabled={disableDelete} size="small" onClick={onDeleteClick} className="tw-h-8 tw-w-8 tw-p-0">
              <FiTrash width={16} height={16} />
            </Button>
          </Tooltip>
        )}
      </Flex>
    </Flex>
  );
};

interface IBANDetailsProps {
  sheba: string;
  status?: string;
  statusText?: string;
  checked?: boolean;
  onClickRadio?: () => void;
  showBankPrefix?: boolean;
  getStatusText: () => string;
}

const IBANDetails: React.FC<IBANDetailsProps> = ({
  sheba,
  status,
  checked,
  onClickRadio,
  showBankPrefix,
  getStatusText,
}) => {
  const { getBankNameByIban } = useBankName();
  const { t } = useAppTranslation();

  const getBankName = () => {
    const bankName = getBankNameByIban(sheba);
    return showBankPrefix ? `${t("profile.bank.bankPrefix")} ${bankName}` : bankName;
  };

  return (
    <Flex vertical gap="4px" className="tw-w-full">
      <Flex align="center" gap="4px">
        <Typography.Text className="tw-text-gray-900 tw-text-xl tw-font-semibold tw-leading-7">
          {formatIBAN(sheba)}
        </Typography.Text>
        {checked || !onClickRadio ? (
          <Tag
            color={getStatusColor(status || "")}
            className="tw-max-w-max tw-px-2 tw-py-0.5 tw-text-xs tw-font-normal tw-leading-5"
          >
            {getStatusText()}
          </Tag>
        ) : (
          <div />
        )}
      </Flex>
      <Flex align="center" gap="4px" className="tw-opacity-50">
        <Bank.Icon iban={sheba} width={16} />
        <Typography.Text className="tw-text-gray-900 tw-text-xs tw-font-normal tw-leading-5">
          {getBankName()}
        </Typography.Text>
      </Flex>
    </Flex>
  );
};

interface SettlementWarningProps {
  showSettlementWarning?: boolean;
  status?: string;
  checked?: boolean;
}

const SettlementWarning: React.FC<SettlementWarningProps> = ({ showSettlementWarning, status, checked }) => {
  const { t } = useAppTranslation();

  if (!showSettlementWarning || status !== "active" || !checked) {
    return null;
  }

  return (
    <Alert
      type="warning"
      message={t("profile.bank.activeIBANSettlementWarning")}
      showIcon={false}
      className="tw-mt-2"
    />
  );
};

interface RejectedStatusSectionProps {
  errorMessage?: string;
  onRetry?: () => void;
  retryLoading?: boolean;
  disableDelete?: boolean;
  isActiveIBAN?: boolean;
  onDeleteClick: (e: React.MouseEvent) => void;
}

const RejectedStatusSection: React.FC<RejectedStatusSectionProps> = ({
  errorMessage,
  onRetry,
  retryLoading,
  disableDelete,
  isActiveIBAN,
  onDeleteClick,
}) => {
  const { t } = useAppTranslation();

  return (
    <div className="tw-flex tw-w-full tw-flex-col tw-gap-4">
      {errorMessage && (
        <div className="tw-flex tw-w-full tw-flex-col tw-gap-1">
          <div className="tw-flex tw-items-center tw-gap-1">
            <CloseCircleOutlined width={16} height={16} style={{ color: "#F5222D" }} className="tw-flex-shrink-0" />
            <Typography.Text className="tw-text-gray-600 !tw-text-xs tw-leading-5">
              {t("profile.bank.errorAndDetails")}
            </Typography.Text>
          </div>
          <Typography.Text className="tw-text-gray-900 tw-text-right !tw-text-xs tw-leading-5">
            {errorMessage}
          </Typography.Text>
        </div>
      )}
      <div className="tw-flex tw-items-start tw-gap-4">
        <Button
          type="link"
          size="small"
          loading={retryLoading}
          onClick={onRetry}
          className="!tw-text-blue-600 tw-leading-5.5 !tw-h-8 !tw-p-1 !tw-text-sm"
        >
          {t("profile.bank.retry")}
        </Button>
        <Tooltip title={disableDelete && isActiveIBAN ? t("profile.bank.cannotDeleteActiveIBAN") : ""}>
          <Button
            type="link"
            size="small"
            onClick={onDeleteClick}
            disabled={disableDelete}
            className="!tw-text-blue-600 tw-leading-5.5 !tw-h-8 !tw-p-1 !tw-text-sm"
          >
            {t("profile.bank.delete")}
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};

interface DeleteConfirmModalProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({ open, onCancel, onConfirm }) => {
  const { t } = useAppTranslation();

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={400}
      centered
      className="delete-confirm-modal"
      maskClosable={true}
      closable={false}
    >
      <div>
        <div className="tw-mb-6 tw-flex tw-items-start tw-gap-2">
          <InfoCircleFilled width={16} height={16} className="tw-text-color-warning" />
          <Typography.Text className="tw-text-base tw-font-medium">
            {t("profile.bank.deleteConfirmation")}
          </Typography.Text>
        </div>

        <div className="tw-flex tw-justify-end tw-gap-3">
          <Button
            onClick={onCancel}
            className="tw-border-gray-300 tw-text-gray-700 hover:tw-border-gray-400 hover:tw-text-gray-800"
          >
            {t("profile.bank.no")}
          </Button>
          <Button
            type="primary"
            danger
            onClick={onConfirm}
            className="tw-bg-red-500 hover:tw-bg-red-600 tw-border-red-500 hover:tw-border-red-600"
          >
            {t("profile.bank.yesDelete")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

const ShebaCard: React.FC<ShebaCardProps> = ({
  sheba,
  status,
  statusText,
  errorMessage,
  onDelete,
  showBankPrefix = true,
  onClickRadio,
  checked,
  disabled,
  title,
  showSettlementWarning = false,
  disableDelete = false,
  hideDeleteIcon = false,
  onRetry,
  retryLoading = false,
  isActiveIBAN = false,
}) => {
  const { t } = useAppTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const getStatusText = (): string => {
    if (statusText) return statusText;
    return status === "active" ? t("profile.bank.active") : status || "";
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    onDelete?.();
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  return (
    <>
      <Card.Ant
        onClick={() => {
          if (onClickRadio) {
            onClickRadio();
          }
        }}
        className="tw-w-full"
        style={{ cursor: onClickRadio ? "pointer" : "default" }}
        styles={{ body: { padding: "16px" } }}
      >
        <Flex justify="space-between" gap="16px" className="tw-w-full">
          {onClickRadio && (
            <div className="tw-flex tw-items-start tw-justify-start tw-gap-2">
              <div className="tw-flex tw-flex-col tw-items-start tw-justify-start tw-overflow-hidden tw-pb-0.5 tw-pt-0.5">
                <Radio checked={checked} disabled={disabled} />
              </div>
            </div>
          )}

          <Flex vertical gap="16px" className="tw-flex-1">
            <CardHeader
              title={title}
              onDelete={onDelete}
              hideDeleteIcon={hideDeleteIcon}
              checked={checked}
              disableDelete={disableDelete}
              isActiveIBAN={isActiveIBAN}
              onDeleteClick={handleDeleteClick}
            />

            <IBANDetails
              sheba={sheba}
              status={status}
              statusText={statusText}
              checked={checked}
              onClickRadio={onClickRadio}
              showBankPrefix={showBankPrefix}
              getStatusText={getStatusText}
            />

            <SettlementWarning showSettlementWarning={showSettlementWarning} status={status} checked={checked} />

            {status === "rejected" && (
              <RejectedStatusSection
                errorMessage={errorMessage}
                onRetry={onRetry}
                retryLoading={retryLoading}
                disableDelete={disableDelete}
                isActiveIBAN={isActiveIBAN}
                onDeleteClick={handleDeleteClick}
              />
            )}
          </Flex>
        </Flex>
      </Card.Ant>

      <DeleteConfirmModal open={showDeleteConfirm} onCancel={handleCancelDelete} onConfirm={handleConfirmDelete} />
    </>
  );
};

export default ShebaCard;
